import { useCallback, useState, useEffect } from "react";
import { useStores } from "../../../stores/setup/use-store";

export function useAlertCreationUsers(options?: { skipAutoSelect?: boolean }) {
    const skipAutoSelect = options?.skipAutoSelect ?? false;

    const {
        dataStores: { insightAlertStore, authStore },
    } = useStores();

    const userId = authStore.userId;
    const [usersTableSelected, setUsersTableSelected] = useState<React.Key[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);

    // Auto-select current user when users are loaded (only for creation flow)
    useEffect(() => {
        if (skipAutoSelect) return;
        if (!isInitialized && insightAlertStore.operationRuleAlertUser.length > 0) {
            const currentUser = insightAlertStore.operationRuleAlertUser.find(
                (user) => user.userId === userId
            );

            if (currentUser) {
                setUsersTableSelected([userId]);
                insightAlertStore.setOperationRuleAlertUserSelected([currentUser]);
                setIsInitialized(true);
            }
        }
    }, [insightAlertStore.operationRuleAlertUser, userId, isInitialized, insightAlertStore, skipAutoSelect]);

    const clearUserTableSelected = useCallback(() => {
        setUsersTableSelected([]);
        insightAlertStore.setOperationRuleAlertUserSelected([]);
        setIsInitialized(false);
    }, [insightAlertStore]);

    const updateUsersTableSelected = useCallback((selectedKeys: React.Key[]) => {
        setUsersTableSelected(selectedKeys);
        // Mark as initialized so auto-select doesn't overwrite
        // (e.g., when edit drawer syncs recipients from the API)
        if (selectedKeys.length > 0) {
            setIsInitialized(true);
        }
    }, []);

    const loadAlertUsers = useCallback(async (lang?: string) => {
        await insightAlertStore.getOperationRuleAlertUserAPI({ lang });
    }, [insightAlertStore]);

    /**
     * Manually select the current logged-in user.
     * Used when enabling alerts on an event that had no alert (edit flow).
     */
    const selectCurrentUser = useCallback(() => {
        const currentUser = insightAlertStore.operationRuleAlertUser.find(
            (user) => user.userId === userId
        );
        if (currentUser) {
            setUsersTableSelected([userId]);
            insightAlertStore.setOperationRuleAlertUserSelected([currentUser]);
            setIsInitialized(true);
        }
    }, [insightAlertStore, userId]);

    return {
        usersTableSelected,
        setUsersTableSelected: updateUsersTableSelected,
        clearUserTableSelected,
        alertUsers: insightAlertStore.operationRuleAlertUser,
        loadAlertUsers,
        selectCurrentUser,
    };
}
